import {
  describeRequestToBookMix,
  type RequestToBookMixNotice,
} from "@baluu/ui/lib/checkout/request-to-book";
import { basketStore, type BasketItem } from "../stores/basket-store";
import { openBasketCheckout } from "./open-basket-checkout";

// The two ways what the visitor chose goes on from an overlay — every
// overlay (class, pass, product, gift card) takes one of them:
//
// - "Add to basket" (a surface with the basket on): the lines join whatever
//   is already in the basket, and the visitor carries on browsing.
// - "Book now" / "Buy now": the basket checkout opens to pay. With the basket
//   on, for everything in it — the lines join the basket first, as the hosted
//   widget's checkout does. With it off (every other embed), for these lines
//   alone: earlier lines for the school are cleared first, the single-item
//   lane the components have always taken.
//
// Request-to-book lines (the school confirms before taking payment) can't
// share a basket with lines paid now — the server refuses the checkout
// (REQUEST_TO_BOOK_MIXED_BASKET) — so lines that would mix them are refused
// here, with the shared notice saying why and what to remove, before the
// visitor finds out at the last step. The rule is the hosted widget's
// (describeRequestToBookMix), a line that mixes on its own included: it's
// refused even into an empty basket, as the server would refuse it.
//
// All or nothing: several lines from one pass (a multi-date booking) are
// checked together and either all go in or none does, so a refusal never
// leaves part of a booking in the basket.

export type BasketLine = Omit<BasketItem, "id" | "addedAt">;

export type LaneOutcome =
  | { ok: true; items: BasketItem[] }
  | { ok: false; notice: RequestToBookMixNotice };

/** "Add to basket": the lines join the school's basket, all or none. */
export function addLinesToBasket(lines: readonly BasketLine[], businessId: string): LaneOutcome {
  const owned = lines.map((line) => ({ ...line, schoolId: line.schoolId || businessId }));
  const notice = describeRequestToBookMix(basketStore.getItems(businessId), owned);
  if (notice) return { ok: false, notice };
  return { ok: true, items: owned.map((line) => basketStore.addItem(line)) };
}

/** "Add to basket" for one line. */
export function addLineToBasket(line: BasketLine, businessId: string): LaneOutcome {
  return addLinesToBasket([line], businessId);
}

/** "Book now" / "Buy now": the basket checkout opens to pay for the lines —
 *  with what's already in the basket when `keepBasket`, alone otherwise.
 *  Refused lines leave the basket as it was and open nothing. */
export function checkoutLines({
  lines,
  businessId,
  apiUrl,
  primaryColor,
  keepBasket,
}: {
  lines: readonly BasketLine[];
  businessId: string;
  apiUrl?: string;
  primaryColor?: string;
  keepBasket: boolean;
}): LaneOutcome {
  const owned = lines.map((line) => ({ ...line, schoolId: line.schoolId || businessId }));
  const base = keepBasket ? basketStore.getItems(businessId) : [];
  const notice = describeRequestToBookMix(base, owned);
  if (notice) return { ok: false, notice };
  if (!keepBasket) basketStore.clearBasket(businessId);
  const items = owned.map((line) => basketStore.addItem(line));
  openBasketCheckout({ businessId, apiUrl, primaryColor });
  return { ok: true, items };
}

/** "Book now" / "Buy now" for one line. */
export function checkoutLine({ line, ...rest }: Omit<Parameters<typeof checkoutLines>[0], "lines"> & { line: BasketLine }): LaneOutcome {
  return checkoutLines({ lines: [line], ...rest });
}
